/**
 * TUS Upload Handler
 *
 * Handles video upload using TUS protocol with resumable uploads.
 * Flow:
 * 1. Request upload URL from backend (/api/video/upload-url)
 * 2. Upload file using TUS protocol
 * 3. Get final progress from backend (/api/video/{id}/progress)
 *
 * @module upload-tus
 */

import * as tus from 'tus-js-client'
import { get as getCookie } from 'es-cookie'
import URL from 'src/api/url'
import uploadState from './upload-state'
import { LOG_EMOJI } from './upload-config'

/**
 * Request upload URL from backend
 * @param {File} file - File to upload
 * @param {string|null} parentFolderId - Parent folder ID
 * @returns {Promise<Object>} Response with uploadUrl and videoId
 */
async function requestUploadUrl(file, parentFolderId) {
  try {
    const token = getCookie('token')
    const authHeader = token
      ? `Bearer ${token}`
      : 'Basic YXN0YXJ1czphc3RhcnVzc2VjcmV0'

    const requestBody = {
      fileName: file.name,
      contentLength: file.size,
    }

    // Add parentFolderId if provided
    if (parentFolderId) {
      requestBody.parentFolderId = parentFolderId
    }

    const response = await fetch(`${URL.API}video/upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader,
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        `Failed to get upload URL: ${response.status} ${response.statusText} - ${errorText}`,
      )
    }

    const responseData = await response.json()

    // Extract data from response wrapper
    const { data } = responseData
    if (!data) {
      throw new Error('Invalid response: missing data object')
    }

    // Return uploadUrl and videoId (id field in response)
    return {
      uploadUrl: data.uploadUrl,
      videoId: data.id,
      completeUri: data.completeUri, // Vimeo video URI
    }
  } catch (error) {
    throw error
  }
}

/**
 * Start SSE connection to monitor backend progress with retry mechanism
 * @param {string} videoId - Video ID
 * @param {string} uploadId - Upload identifier
 * @param {Function} progressCallback - Progress update callback
 * @param {Function} resolve - Promise resolve function
 * @param {Function} reject - Promise reject function
 * @param {number} retryCount - Current retry attempt (default: 0)
 */
function startProgressSSE(
  videoId,
  uploadId,
  progressCallback,
  resolve,
  reject,
  retryCount = 0,
) {
  const MAX_RETRIES = 10
  const RETRY_DELAY = 2000 // 2 seconds

  try {
    const token = getCookie('token')
    const progressUrl = `${URL.API}video/${videoId}/progress`

    console.log(
      `${LOG_EMOJI.START} Starting SSE connection to: ${progressUrl} (attempt ${
        retryCount + 1
      }/${MAX_RETRIES + 1})`,
    )

    const eventSource = new EventSource(progressUrl)

    // Store EventSource reference for cleanup
    uploadState.setEventSource(uploadId, eventSource)

    let lastReceivedData = null
    let connectionOpened = false

    eventSource.onmessage = event => {
      try {
        const progressData = JSON.parse(event.data)
        lastReceivedData = progressData

        console.log(
          `${LOG_EMOJI.SUCCESS} SSE progress update (attempt ${
            retryCount + 1
          }):`,
          progressData,
        )

        // Call progress callback with status update
        progressCallback({
          percentComplete: 100,
          message: getStatusMessage(progressData.status),
          status: progressData.status,
          details: progressData.details,
          ...progressData,
        })

        // Check if processing is complete
        if (
          progressData.status === 'available' ||
          progressData.status === 'completed'
        ) {
          console.log(
            `${LOG_EMOJI.SUCCESS} Video processing completed: ${videoId}`,
          )
          eventSource.close()
          uploadState.deleteEventSource(uploadId)
          uploadState.markAsComplete(uploadId)
          resolve({
            ...progressData,
            videoId,
          })
        }
      } catch (error) {
        console.error(`${LOG_EMOJI.ERROR} Error parsing SSE message:`, error)
      }
    }

    eventSource.onerror = error => {
      console.error(
        `${LOG_EMOJI.ERROR} SSE error for video ${videoId} (attempt ${
          retryCount + 1
        }):`,
        error,
      )

      // Close current connection
      eventSource.close()
      uploadState.deleteEventSource(uploadId)

      // Check if we should retry
      if (retryCount < MAX_RETRIES) {
        console.log(
          `${LOG_EMOJI.RETRY} Retrying SSE connection in ${
            RETRY_DELAY / 1000
          }s... (attempt ${retryCount + 2}/${MAX_RETRIES + 1})`,
        )

        // Wait before retry
        setTimeout(() => {
          // Check if upload was cancelled during wait
          if (uploadState.isCancelling(uploadId)) {
            console.log(
              `${LOG_EMOJI.CANCEL} Upload cancelled during retry wait: ${uploadId}`,
            )
            const err = new Error('Upload cancelled')
            err.isAborted = true
            reject(err)
            return
          }

          // Check if we're still online
          if (!navigator.onLine) {
            console.log(
              `${LOG_EMOJI.NETWORK} Offline detected, waiting for connection...`,
            )
            progressCallback({
              percentComplete: 100,
              message: 'Waiting for internet connection...',
              status: lastReceivedData?.status || 'processing',
              isWaitingForNetwork: true,
            })

            // Wait for online event
            const onlineHandler = () => {
              window.removeEventListener('online', onlineHandler)
              console.log(
                `${LOG_EMOJI.NETWORK} Connection restored, retrying SSE...`,
              )
              startProgressSSE(
                videoId,
                uploadId,
                progressCallback,
                resolve,
                reject,
                retryCount + 1,
              )
            }
            window.addEventListener('online', onlineHandler)
            return
          }

          // Retry the connection
          startProgressSSE(
            videoId,
            uploadId,
            progressCallback,
            resolve,
            reject,
            retryCount + 1,
          )
        }, RETRY_DELAY)
      } else {
        // Max retries exceeded
        console.error(
          `${LOG_EMOJI.ERROR} Max SSE retries (${MAX_RETRIES}) exceeded for video: ${videoId}`,
        )

        const err = new Error(
          `Failed to connect to progress stream after ${MAX_RETRIES} retries`,
        )
        err.isNetworkError = true
        reject(err)
      }
    }

    eventSource.onopen = () => {
      connectionOpened = true
      console.log(
        `${
          LOG_EMOJI.SUCCESS
        } SSE connection opened for video: ${videoId} (attempt ${
          retryCount + 1
        })`,
      )
    }
  } catch (error) {
    console.error(`${LOG_EMOJI.ERROR} SSE setup error:`, error)

    // Retry on setup error if retries available
    if (retryCount < MAX_RETRIES) {
      console.log(
        `${LOG_EMOJI.RETRY} Setup failed, retrying in ${
          RETRY_DELAY / 1000
        }s...`,
      )
      setTimeout(() => {
        startProgressSSE(
          videoId,
          uploadId,
          progressCallback,
          resolve,
          reject,
          retryCount + 1,
        )
      }, RETRY_DELAY)
    } else {
      reject(error)
    }
  }
}

/**
 * Get user-friendly message for status
 * @param {string} status - Status code
 * @returns {string} User-friendly message
 */
function getStatusMessage(status) {
  switch (status) {
    case 'uploading':
      return 'Uploading...'
    case 'transcode_starting':
      return 'Transcoding...'
    case 'transcoding':
      return 'Transcoding...'
    case 'available':
      return 'Video ready!'
    case 'completed':
      return 'Processing complete!'
    default:
      return 'Processing...'
  }
}

/**
 * Get final progress from backend
 * @param {string} videoId - Video ID
 * @returns {Promise<Object>} Final progress data
 */
async function getFinalProgress(videoId) {
  try {
    const token = getCookie('token')
    const authHeader = token
      ? `Bearer ${token}`
      : 'Basic YXN0YXJ1czphc3RhcnVzc2VjcmV0'

    const response = await fetch(`${URL.API}video/${videoId}/progress`, {
      method: 'GET',
      headers: {
        Authorization: authHeader,
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        `Failed to get progress: ${response.status} ${response.statusText} - ${errorText}`,
      )
    }

    const responseText = await response.text()

    // Parse SSE format: handle multiple "data: {...}" lines
    let data
    if (responseText.includes('data: ')) {
      // SSE format - get the last data line (most recent)
      const lines = responseText
        .split('\n')
        .filter(line => line.trim().startsWith('data: '))
      if (lines.length > 0) {
        const lastLine = lines[lines.length - 1]
        const jsonStr = lastLine.substring(6).trim() // Remove "data: " prefix
        data = JSON.parse(jsonStr)
      } else {
        throw new Error('No valid SSE data lines found')
      }
    } else {
      data = JSON.parse(responseText)
    }

    return data
  } catch (error) {
    throw error
  }
}

/**
 * Create and configure TUS upload
 * @param {File} file - File to upload
 * @param {string} uploadId - Upload identifier
 * @param {Function} progressCallback - Progress update callback
 * @param {string|null} parentFolderId - Parent folder ID
 * @returns {Promise<Object>} Upload result
 */
export function createTusUpload(
  file,
  uploadId,
  progressCallback,
  parentFolderId = null,
) {
  return new Promise(async (resolve, reject) => {
    try {
      // ========== STEP 1: Request upload URL from backend ==========
      const uploadData = await requestUploadUrl(file, parentFolderId)
      const { uploadUrl, videoId, completeUri } = uploadData

      if (!uploadUrl || !videoId) {
        throw new Error(
          'Invalid response from upload-url endpoint: missing uploadUrl or videoId',
        )
      }

      const startTime = Date.now()

      // ========== STEP 2: Create TUS upload ==========
      const upload = new tus.Upload(file, {
        uploadUrl: uploadUrl,
        metadata: {
          filename: file.name,
          filetype: file.type,
        },
        onError: error => {
          uploadState.deleteTusUpload(uploadId)

          const err = new Error(error.message || 'Upload failed')
          err.originalError = error
          reject(err)
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          const percentComplete = Math.round((bytesUploaded / bytesTotal) * 100)
          const uploadSpeed = bytesUploaded / ((Date.now() - startTime) / 1000) // bytes per second

          progressCallback({
            percentComplete,
            bytesProcessed: bytesUploaded,
            bytesTotal,
            message: `Uploading: ${percentComplete}%`,
            uploadSpeed: uploadSpeed,
            videoId,
            completeUri,
          })
        },
        onSuccess: async () => {
          console.log(
            `${LOG_EMOJI.SUCCESS} TUS upload completed, starting SSE monitoring for: ${videoId}`,
          )
          uploadState.deleteTusUpload(uploadId)

          try {
            // ========== STEP 3: Start SSE monitoring for backend processing ==========
            startProgressSSE(
              videoId,
              uploadId,
              progressCallback,
              resolve,
              reject,
            )
          } catch (error) {
            reject(error)
          }
        },
      })

      // Store TUS upload reference for cancellation
      uploadState.setTusUpload(uploadId, upload)

      // Start the upload
      upload.start()
    } catch (error) {
      uploadState.deleteTusUpload(uploadId)
      reject(error)
    }
  })
}

/**
 * Cancel an active TUS upload
 * @param {string} uploadId - Upload identifier
 * @returns {Promise<boolean>} True if upload was found and cancelled
 */
export async function cancelTusUpload(uploadId) {
  try {
    const upload = uploadState.getTusUpload(uploadId)
    if (upload) {
      // Abort the upload (this will trigger onError callback)
      await upload.abort()

      uploadState.deleteTusUpload(uploadId)
      return true
    }

    return false
  } catch (error) {
    uploadState.deleteTusUpload(uploadId)
    throw error
  }
}
